import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ExecWordmark } from "@/components/ExecChrome";

const labelClass = "block text-[11px] uppercase tracking-[0.14em] font-normal text-[color:var(--grey)]";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/hub";
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.email, form.password, form.name);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="login-page">
      <Seo title="Sign in — Copper & Vine" description="Sign in to the Copper & Vine Hub." noindex />
      <div className="exec-container pt-24 md:pt-28 pb-24 max-w-md">
        <Link to="/" className="inline-block text-[20px]">
          <ExecWordmark />
        </Link>
        <p className="exec-eyebrow mt-9">{mode === "login" ? "The Hub" : "Create access"}</p>
        <h1 className="font-extralight text-[clamp(30px,4vw,46px)] leading-[1.1] mt-4">
          {mode === "login" ? "Sign in" : "Create your account"}
        </h1>
        <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4">
          {mode === "login"
            ? "Your engagement, deliverables, and the Operating Room archive."
            : "For members and buyers. If you purchased, set your password from the receipt page."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          {mode === "register" && (
            <label className="block">
              <span className={labelClass}>Name</span>
              <input
                data-testid="auth-name-input"
                className="exec-underline mt-1.5"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
          )}
          <label className="block">
            <span className={labelClass}>Email</span>
            <input
              data-testid="auth-email-input"
              type="email"
              className="exec-underline mt-1.5"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Password</span>
            <input
              data-testid="auth-password-input"
              type="password"
              className="exec-underline mt-1.5"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
          </label>
          {error && (
            <p data-testid="auth-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3">
              {error}
            </p>
          )}
          <button data-testid="auth-submit-button" type="submit" disabled={busy} className="exec-btn exec-btn-primary w-full disabled:opacity-60">
            {busy ? "\u2026" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <button
          data-testid="auth-toggle-mode"
          className="exec-link mt-7"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setError("");
          }}
        >
          {mode === "login" ? "Need an account? Create one" : "Have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
